import authService from './authService';
import config from '../config';

/**
 * Manage references (add/remove) between resources.
 * @param {Array<Object>} actions - An array of reference actions.
 * @returns {Promise<Object>} - The result of the management operation (ManageReferencesResponse schema).
 */
export const manageReferences = async (actions) => {
  const url = `${config.API_BASE_URL}/api/v1/references/manage`;
  try {
    console.log("Managing references:", { actions });
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: { actions }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to manage references: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error managing references:', error);
    throw error;
  }
};

/**
 * Fetches all references where the given resource ID is the parent.
 * @param {string} parentId - The ID of the parent resource.
 * @returns {Promise<Array<Object>>} - An array of reference objects.
 */
export const getReferencesByParent = async (parentId) => {
  if (!parentId) {
    throw new Error("Parent ID is required to fetch references.");
  }
  const queryParams = new URLSearchParams({ parent_id: parentId });
  return getReferences(queryParams);
};

/**
 * Deletes a single reference by its unique ID.
 * @param {string} referenceId - The unique ID of the reference link to delete.
 * @returns {Promise<boolean>} - True if deletion was successful (status 204), false otherwise.
 * @throws {Error} If authentication fails, reference not found, or other API errors occur.
 */
export const deleteReferenceById = async (referenceId) => {
  if (!referenceId) {
    throw new Error('Reference ID is required for deletion.');
  }
  const url = `${config.API_BASE_URL}/api/v1/references/${referenceId}`;
  console.log(`Attempting DELETE reference: ${url}`);
  try {
    const response = await authService.authFetch(url, { method: 'DELETE' });
    if (response.status === 204) {
      console.log(`Successfully deleted reference ${referenceId}`);
      return true;
    }
    if (response.status === 404) {
      throw new Error(`Reference with ID ${referenceId} not found.`);
    }
    if (response.status === 403) {
        throw new Error(`Permission denied to delete reference ${referenceId}.`);
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete reference ${referenceId}: ${response.status}`);
  } catch (error) {
    console.error(`Error deleting reference ${referenceId}:`, error);
    throw error;
  }
};

/**
 * Fetches all references where the given resource ID is the child.
 * @param {string} childId - The ID of the child resource.
 * @param {string} [childType] - Optional type of the child resource for a more specific query.
 * @returns {Promise<Array<Object>>} - An array of reference objects.
 */
export const getReferencesByChild = async (childId, childType) => {
  if (!childId) {
    throw new Error("Child ID is required to fetch references.");
  }
  const queryParams = new URLSearchParams({ child_id: childId });
  if (childType) {
    queryParams.append('child_type', childType);
  }
  return getReferences(queryParams);
};

/**
 * Fetches references based on a flexible set of query parameters.
 * @param {URLSearchParams} queryParams - The query parameters for filtering.
 * @returns {Promise<Array<Object>>} - An array of reference objects.
 */
export const getReferences = async (queryParams) => {
  if (!queryParams || queryParams.toString() === '') {
    console.warn("[referenceApi] getReferences called with no query parameters. This might return a large dataset.");
  }
  const url = `${config.API_BASE_URL}/api/v1/references?${queryParams.toString()}`;
  console.log(`[referenceApi] Fetching references from: ${url}`);
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch references: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching references with params ${queryParams.toString()}:`, error);
    authService.trackNetworkError('getReferences', error);
    throw error;
  }
};


export default {
  manageReferences,
  deleteReferenceById,
  getReferencesByParent,
  getReferencesByChild,
  getReferences,
};